import { useCallback, useEffect, useRef, useState } from "react"

export interface BulkStatus {
  status: string
  totalCount: number
  sentCount: number
  failedCount: number
  failedNumbers: string[]
}

export type BulkSendPhase = "idle" | "sending" | "complete"

interface Progress {
  sentCount: number
  failedCount: number
  totalCount: number
}

interface Options {
  pollStatus: (batchId: number) => Promise<BulkStatus | null>
  onComplete: (final: BulkStatus) => void
  intervalMs?: number
}

const isTerminal = (status?: string) => status === "completed" || status === "failed"

export function useBulkSendProgress({ pollStatus, onComplete, intervalMs = 2000 }: Options) {
  const [phase, setPhase] = useState<BulkSendPhase>("idle")
  const [progress, setProgress] = useState<Progress>({ sentCount: 0, failedCount: 0, totalCount: 0 })
  const timer = useRef<ReturnType<typeof setInterval> | null>(null)
  // Tracks whether the component is still mounted so a late poll cannot update state
  const aliveRef = useRef(true)
  // The callback is read through a ref so the interval always calls the latest one
  const onCompleteRef = useRef(onComplete)
  onCompleteRef.current = onComplete
  const pollRef = useRef(pollStatus)
  pollRef.current = pollStatus

  const stop = useCallback(() => {
    if (timer.current) { clearInterval(timer.current); timer.current = null }
  }, [])

  const reset = useCallback(() => {
    stop()
    setPhase("idle")
    setProgress({ sentCount: 0, failedCount: 0, totalCount: 0 })
  }, [stop])

  const start = useCallback((batchId: number, totalCount: number) => {
    stop()
    setPhase("sending")
    setProgress({ sentCount: 0, failedCount: 0, totalCount })
    // Extracted so both the immediate call and setInterval share the same body
    const tick = async () => {
      const s = await pollRef.current(batchId)
      // Bail out if the component unmounted while the poll was in flight
      if (!aliveRef.current) return
      // A missed read is transient so keep the loop running
      if (!s) return
      setProgress({ sentCount: s.sentCount, failedCount: s.failedCount, totalCount: s.totalCount })
      if (isTerminal(s.status)) {
        stop()
        setPhase("complete")
        onCompleteRef.current(s)
      }
    }
    // Fire once immediately so the UI reflects the first reading without waiting
    tick()
    timer.current = setInterval(tick, intervalMs)
  }, [intervalMs, stop])

  // Mark alive on mount and remount, clear the interval and mark unmounted on cleanup
  // Setting alive true in the body restores it after a strict-mode remount so polls keep updating
  useEffect(() => {
    aliveRef.current = true
    return () => {
      aliveRef.current = false
      stop()
    }
  }, [stop])

  return { phase, progress, start, reset }
}
